import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { TaskService } from '../../services/task.service';
import { ITask, TaskStatus } from '../../interfaces/task.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import TaskListTable from './TaskListTable';
import TaskForm from './TaskForm';

const PAGE_SIZE = 10;

const TasksAssignedByMe: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ITask | null>(null);

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await TaskService.assignedByMe(institutionId, p, PAGE_SIZE);
            setTasks(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (t: ITask) => { setEditing(t); setFormOpen(true); };

    const handleDelete = async (t: ITask) => {
        if (!window.confirm(`Delete task "${t.title}"?`)) return;
        try {
            await TaskService.delete(t.id, institutionId);
            toast.showSuccess('Task deleted');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to delete task');
        }
    };

    const handleStatusChange = async (t: ITask, next: TaskStatus, blockedReason?: string) => {
        try {
            await TaskService.updateStatus(t.id, next, blockedReason);
            toast.showSuccess('Status updated');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to update status');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Tasks I Assigned</Typography>
                <Button data-help="tasks.assigned.newTask" variant="contained" onClick={openCreate}>New Task</Button>
            </Box>
            <TaskListTable
                tasks={tasks}
                loading={loading}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                mode="assigner"
                onStatusChange={handleStatusChange}
                onEdit={openEdit}
                onDelete={handleDelete}
            />
            <TaskForm
                open={formOpen}
                task={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => fetchList(page)}
            />
        </Box>
    );
};

export default TasksAssignedByMe;
